import { BaseDto } from '../base.dto'
import { PagingDto } from '../paging.dto'
import { FileDto } from '../file.dto'

// Mall interface for facility booking API response
export interface MallFacilityMall {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  slug: string
  description: string
  address: string
  logo: FileDto
  image: FileDto
  latitude: number
  longitude: number
  paywayMallId: string
}

// Minimal user shape used within facility booking responses
export interface MallFacilityBookingUser {
  id?: string
  fullName?: string
  email?: string
  phoneNumber?: string
}

// Main facility booking DTO
export class MallFacilityDto extends BaseDto {
  code: string
  status: number
  useStatus: number
  facilityId: string
  facilityType: string
  facilityStatus: number
  mall: MallFacilityMall
  // Optional current booking info for this facility item
  currentBooking?: {
    id: string
    userId: string
    name: string
    confirmedAt?: string | null
    bookingCode?: string | null
    createdAt?: string | null
    user?: MallFacilityBookingUser | null
  } | null

  static create(data: Partial<MallFacilityDto>): MallFacilityDto {
    return Object.assign(new MallFacilityDto(), {
      id: data.id || null,
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null,
      code: data.code || '',
      status: data.status || 0,
      useStatus: data.useStatus || 0,
      facilityId: data.facilityId || '',
      facilityType: data.facilityType || '',
      facilityStatus: data.facilityStatus || 0,
      mall: data.mall || null,
      currentBooking: data.currentBooking ?? null
    })
  }
}

// Response DTO for facility booking list
export class MallFacilityResponseDto {
  count: number
  rows: MallFacilityDto[]
}

// Find/Filter DTO for facility booking
export class FindMallFacilityDto extends PagingDto {
  type?: string
  search?: string
  status?: number
  useStatus?: number
  mallId?: string
  facilityType?: string
}

// Create DTO for facility booking
export class CreateMallFacilityDto {
  code: string
  status: number
  useStatus: number
  facilityId: string
  facilityType: string
  facilityStatus: number
  mallId: string
}

// Update DTO for facility booking
export class UpdateMallFacilityDto {
  code?: string
  status?: number
  useStatus?: number
  facilityId?: string
  facilityType?: string
  facilityStatus?: number
  mallId?: string
}

export class UpdateMallFacilityStatusDto {
  status: number
}

// New create-multiple by type & count API
export class CreateMallFacilitiesRequestDto {
  mallId: string
  type: string // 'wheelchair' | 'stroller' | ...
  deviceCount: number
}

export class CreateMallFacilitiesItemDto {
  id: string
  code: string
  status: number
  useStatus: number
}

export class CreateMallFacilitiesResponseDto {
  id: string
  createdAt: string
  updatedAt: string
  mallId: string
  type: string
  status: number
  items: CreateMallFacilitiesItemDto[]
}

// State management for facility booking
export class MallFacilityState {
  facilities: MallFacilityDto[]
  isLoading: boolean
  error: Error | string | null
  pageInfo: {
    page: number
    pageSize: number
    collectionSize: number
    from: number
    to: number
  }
  filters: FindMallFacilityDto
  lastUpdated?: Date | null
}

// =====================================================================================
// New Booking DTOs for mall-facility-bookings list API
// =====================================================================================

export class MallFacilityBookingDto extends BaseDto {
  userId: string
  name: string
  bookingCode: string
  bookingDeviceType?: string | null
  idType: number | null
  idImageId: string | null
  idImage: FileDto | null
  status: number
  confirmedAt: string | null
  rejectedAt: string | null
  note: string | null
  facilityId: string | null
  receivedAt: string | null
  returnedAt: string | null
  user: MallFacilityBookingUser
  facilityItemCode: string | null
  facilityItemType: string | null

  static create(data: Partial<MallFacilityBookingDto>): MallFacilityBookingDto {
    return Object.assign(new MallFacilityBookingDto(), {
      id: MallFacilityBookingDto.or(data.id, null),
      createdAt: MallFacilityBookingDto.nullableString(data.createdAt),
      updatedAt: MallFacilityBookingDto.nullableString(data.updatedAt),
      userId: MallFacilityBookingDto.or(data.userId, ''),
      name: MallFacilityBookingDto.or(data.name, ''),
      bookingCode: MallFacilityBookingDto.or(data.bookingCode, ''),
      bookingDeviceType: data.bookingDeviceType ?? data.facilityItemType ?? null,
      idType: MallFacilityBookingDto.or(data.idType, null),
      idImageId: MallFacilityBookingDto.or(data.idImageId, null),
      idImage: MallFacilityBookingDto.or(data.idImage, null),
      status: MallFacilityBookingDto.or(data.status, 0),
      confirmedAt: MallFacilityBookingDto.nullableString(data.confirmedAt),
      rejectedAt: MallFacilityBookingDto.nullableString(data.rejectedAt),
      note: MallFacilityBookingDto.or(data.note, null),
      facilityId: MallFacilityBookingDto.or(data.facilityId, null),
      receivedAt: MallFacilityBookingDto.nullableString(data.receivedAt),
      returnedAt: MallFacilityBookingDto.nullableString(data.returnedAt),
      user: MallFacilityBookingDto.or<MallFacilityBookingUser>(data.user, {} as MallFacilityBookingUser),
      facilityItemCode: MallFacilityBookingDto.or(data.facilityItemCode, null),
      facilityItemType: MallFacilityBookingDto.or(data.facilityItemType, null)
    })
  }

  private static or<T>(value: T | undefined, fallback: T): T {
    return value ?? fallback;
  }

  private static nullableString(value: string | Date | null | undefined): string | null {
    return typeof value === 'string' ? value : null;
  }
}

export class FindMallFacilityBookingDto extends PagingDto {
  status?: number
  searchText?: string
  fromTime?: string
  toTime?: string
  facilityType?: string
}

export class MallFacilityBookingResponseDto {
  count: number
  rows: MallFacilityBookingDto[]
}

// Update booking status/body
export class UpdateMallFacilityBookingDto {
  status?: number
  note?: string | null
  facilityItemId?: string | null
}
